import { useXhr } from '@fullstory/react-hooks';
import { NetworkController } from '@fullstory/util/xhr';
import {
  QueryClient,
  QueryKey,
  queryOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { buildExecuteRequestOptions, processGRPCRequest, processRequest } from './request';
import { isGRPCRequestConfig } from './requestConfig';
import { GRPCRequestConfig, RequestConfig } from './types';
import { cacheKeyToQueryKey, grpcRequestConfigCacheKey, requestConfigCacheKey } from './utils';

/**
 * Partially typed {@link UseQueryOptions} that work with fullstory's request framework.
 */
export type RequestQueryOptions<
  TQueryFnData,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = ReadonlyArray<string>,
> = UseQueryOptions<TQueryFnData, unknown, TData, TQueryKey>;

/**
 * The subset of {@link RequestQueryOptions} that can be customized.
 */
export type ConfigurableRequestQueryOptions<TQueryFnData, TData = TQueryFnData> = Omit<
  Partial<RequestQueryOptions<TQueryFnData, TData>>,
  'queryFn'
>;

const getGRPCQueryOptions = <TBody, TResponse, TData = TResponse>(
  requestConfig: GRPCRequestConfig<TBody, TResponse>,
  xhr: NetworkController,
  client: QueryClient,
  opts: ConfigurableRequestQueryOptions<TResponse, TData> = {},
): RequestQueryOptions<TResponse, TData> => {
  // construct the queryKey
  const queryKey = opts.queryKey || cacheKeyToQueryKey(grpcRequestConfigCacheKey(requestConfig));

  // Construct queryFn using a backwards-compatible processing function
  // note that queryFn's do not need stable references in react-query
  const options = buildExecuteRequestOptions(client);
  const queryFn = () => processGRPCRequest({ requestConfig, xhr, options });

  return queryOptions({
    ...opts,
    queryKey,
    queryFn,
  });
};

const getLegacyQueryOptions = <TBody, TResponse, TData = TResponse>(
  requestConfig: RequestConfig<TBody, TResponse>,
  queryClient: QueryClient,
  xhr: NetworkController,
  opts: ConfigurableRequestQueryOptions<TResponse, TData> = {},
): RequestQueryOptions<TResponse, TData> => {
  // construct the queryKey
  const queryKey = opts.queryKey || cacheKeyToQueryKey(requestConfigCacheKey(requestConfig));

  // Construct queryFn using a backwards-compatible processing function
  // note that queryFn's do not need stable references in react-query
  const options = buildExecuteRequestOptions(queryClient);
  const queryFn = () => processRequest<TBody, TResponse>({ requestConfig, xhr, options });

  return queryOptions({
    ...opts,
    queryKey,
    queryFn,
  });
};
/**
 * Returns a QueryOptions object for use with react-query's useQuery hook.
 * @param requestConfig The GRPC or legacy request configuration object.
 * @param xhr The network controller to use for handling the request.
 * @param opts Additional {@link UseQueryOptions} to pass to react-query's useQuery. Note that the
 * queryKey and queryFn is automatically generated from the requestConfig. A custom queryKey can optionally
 * be provided, but this will break backwards compatibility with existing cache keys.
 *
 * @generic TBody the type of the request body
 * @generic TResponse the type of the response body
 * @generic TData the type of the data modified by {@link UseQueryOptions.select}
 *
 * @returns Configured {@link UseQueryOptions} to use with react-query's {@link useQuery} hook.
 */
export const getRequestQueryOptions = <TBody, TResponse, TData = TResponse>(
  requestConfig: GRPCRequestConfig<TBody, TResponse> | RequestConfig<TBody, TResponse>,
  queryClient: QueryClient,
  xhr: NetworkController,
  opts?: ConfigurableRequestQueryOptions<TResponse, TData>,
): RequestQueryOptions<TResponse, TData> => {
  if (isGRPCRequestConfig(requestConfig)) {
    return getGRPCQueryOptions(requestConfig, xhr, queryClient, opts);
  }

  return getLegacyQueryOptions(
    requestConfig as RequestConfig<unknown, TResponse>,
    queryClient,
    xhr,
    opts,
  );
};

/**
 * Returns a QueryOptions object for use with react-query's useQuery hook.
 * @param requestConfig The GRPC or legacy request configuration object.
 * @param opts Additional {@link UseQueryOptions} to pass to react-query's useQuery. Note that the
 * queryKey and queryFn is automatically generated from the requestConfig. A custom queryKey can optionally
 * be provided, but this will break backwards compatibility with existing cache keys.
 *
 * @generic TBody the type of the request body
 * @generic TResponse the type of the response body
 * @generic TData the type of the data modified by {@link UseQueryOptions.select}
 *
 * @returns Configured {@link UseQueryOptions} to use with react-query's {@link useQuery} hook.
 */
export const useRequestQueryOptions = <TBody, TResponse, TData = TResponse>(
  requestConfig: GRPCRequestConfig<TBody, TResponse> | RequestConfig<TBody, TResponse>,
  opts?: ConfigurableRequestQueryOptions<TResponse, TData>,
): RequestQueryOptions<TResponse, TData> => {
  const xhr = useXhr();
  const client = useQueryClient();

  return useMemo(
    () => getRequestQueryOptions(requestConfig, client, xhr, opts),
    [requestConfig, client, xhr, opts],
  );
};

/**
 * Returns an array of QueryOptions objects for use with react-query's useQueries hook.
 * @param requestConfigs The GRPC or legacy request configuration objects.
 * @param opts Additional {@link UseQueryOptions} that applies to all {@link RequestQueryOptions}. Note that the
 * queryKey and queryFn is automatically generated from the requestConfig. A custom queryKey can optionally
 * be provided, but this will break backwards compatibility with existing cache keys.
 */
export const useMultiRequestQueryOptions = <TBody, TResponse, TData = TResponse>(
  requestConfigs: GRPCRequestConfig<TBody, TResponse>[] | RequestConfig<TBody, TResponse>[],
  opts?: ConfigurableRequestQueryOptions<TResponse, TData>,
): RequestQueryOptions<TResponse, TData>[] => {
  const xhr = useXhr();
  const client = useQueryClient();

  return useMemo(
    () =>
      requestConfigs.map(requestConfig => getRequestQueryOptions(requestConfig, client, xhr, opts)),
    [requestConfigs, client, xhr, opts],
  );
};
